// Real .xlsx workbook export using SheetJS. Handles headers, column
// widths, number formats (currency, integer, date), and freezes the top
// row so scrolling in Excel/Numbers stays anchored. No dependency on the
// backend: everything is computed client-side from the same rows the CSV
// export uses, so both files carry identical numbers.
//
// Currency is passed as an ISO 4217 code (e.g. "AUD") and resolved to a
// display symbol via Intl.NumberFormat so Excel renders "A$1,234.00"
// for an Australian operator, not "$1,234.00". No symbol is ever
// hardcoded.
import * as XLSX from "xlsx";
import { stampedFilename } from "./csvExport";
import { getCurrency, getCurrencySymbol } from "./prefs";

// Escape any character that has special meaning inside an Excel number
// format code so a currency symbol like "kr" or "R$" cannot break the
// pattern. Only quote the symbol itself.
function escapeSymbolForFmt(symbol) {
  return String(symbol || "$").replace(/"/g, '""');
}

function buildFormats(currencyCode) {
  const sym = escapeSymbolForFmt(getCurrencySymbol(currencyCode));
  return {
    money: `"${sym}"#,##0.00;[Red]-"${sym}"#,##0.00`,
    number: "#,##0.00",
    integer: "#,##0",
    date: "yyyy-mm-dd hh:mm",
  };
}

function toCellValue(v, type, formats) {
  if (v == null || v === "") return { v: "", t: "s" };
  if (type === "money" || type === "number" || type === "integer") {
    const n = Number(v);
    if (Number.isNaN(n)) return { v: String(v), t: "s" };
    return { v: n, t: "n", z: formats[type] };
  }
  if (type === "date") {
    const d = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime())) return { v: String(v), t: "s" };
    // Store as a real Excel date so filters and pivot tables work.
    return { v: d, t: "d", z: formats.date };
  }
  return { v: String(v), t: "s" };
}

function autoWidth(rows, columns) {
  return columns.map((c) => {
    const headerLen = String(c.label).length;
    const cellLens = rows.map((r) => {
      const raw = c.get(r);
      if (raw == null) return 0;
      if (c.type === "money") return Number(raw).toFixed(2).length + 3;
      return String(raw).length;
    });
    const max = Math.max(headerLen, ...cellLens, 8);
    return { wch: Math.min(60, max + 2) };
  });
}

// Builds a worksheet from `rows` + column defs. Sheet name defaults to
// "Sheet1"; caller can override. `currency` is optional: when omitted we
// fall back to the current user preference so a chef in Australia never
// sees USD symbols in their export.
export function buildWorksheet(rows, columns, sheetName = "Sheet1", currency) {
  const formats = buildFormats(currency || getCurrency());
  const aoa = [
    columns.map((c) => c.label),
    ...rows.map((r) => columns.map((c) => c.get(r))),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa.map((row, rowIdx) => {
    if (rowIdx === 0) return row; // header row: raw strings
    return row.map((v, i) => toCellValue(v, columns[i].type, formats));
  }));

  ws["!cols"] = autoWidth(rows, columns);
  // Freeze the header row so scrolling stays anchored.
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };
  // AutoFilter on the header range so Excel adds the drop-down arrows.
  if (rows.length > 0) {
    const lastCol = XLSX.utils.encode_col(columns.length - 1);
    ws["!autofilter"] = { ref: `A1:${lastCol}${rows.length + 1}` };
  }
  return { ws, sheetName };
}

// Downloads a single-sheet workbook.
export function downloadWorkbook(sheets, filenamePrefix) {
  const wb = XLSX.utils.book_new();
  for (const { ws, sheetName } of sheets) {
    XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  }
  const filename = stampedFilename(filenamePrefix, "xlsx");
  XLSX.writeFile(wb, filename, { bookType: "xlsx", compression: true });
}
